import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  useStepperReturn,
} from '@ais-ui/ui-core';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { StepperFooter } from './StepperFooter';
import { useLocalization } from '@ais-ui/locale-management';
import { useCreateReportContext } from './CreateReportContext';

const reportTemplateNamingFormSchema = z.object({
  template_name: z.string().min(1, 'Template name is required'),
  report_name: z.string().optional(),
});

type reportTemplateNamingValues = z.infer<
  typeof reportTemplateNamingFormSchema
>;

interface NamingStepProps {
  onPrevious: ((event: React.MouseEvent<HTMLButtonElement>) => void) | null;
}

export const NamingStep = ({ onPrevious }: NamingStepProps) => {
  const { messages } = useLocalization();

  const {
    stepperProps,
    reportTemplateName,
    firstReportName,
    setReportTemplateName,
    setFirstReportName,
  } = useCreateReportContext();

  const form = useForm<reportTemplateNamingValues>({
    resolver: zodResolver(reportTemplateNamingFormSchema),
    defaultValues: {
      template_name: reportTemplateName,
      report_name: firstReportName,
    },
  });

  const onNext = (data: reportTemplateNamingValues) => {
    setReportTemplateName(data.template_name);

    const reportName = data.report_name ? data.report_name : 'N/A';
    setFirstReportName(reportName);

    stepperProps.nextStep();
  };

  return (
    <>
      <div className="my-4">
        <h2 className="text-lg">{messages.reportsCreateReportNamingTitle}</h2>
        <p className="text-sm text-muted-foreground">
          {messages.reportsCreateReportNamingSubtitle}
        </p>
      </div>

      <Form {...form}>
        <form className="flex flex-col space-y-8 pt-4 h-full">
          <div className="h-[90%]">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="template_name"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportNamingTemplateNameLabel} *
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportNamingTemplateNameTooltip}
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        id="report-template-name"
                        type="text"
                        placeholder={
                          messages.reportsCreateReportNamingTemplateNamePlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="report_name"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportNamingReportNameLabel}
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportNamingReportNameTooltip}
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        type="text"
                        placeholder={
                          messages.reportsCreateReportNamingReportNamePlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <StepperFooter
            onPrevious={onPrevious}
            onNext={form.handleSubmit(onNext)}
            stepperProps={stepperProps}
          ></StepperFooter>
        </form>
      </Form>
    </>
  );
};
